import { useState, useEffect } from "react";
import { Sparkles, TrendingUp, Gift, PartyPopper, Heart, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";

const timelineStages = [
  {
    age: "Naissance",
    year: 0,
    moments: 1,
    capitalInvesti: "€3,200",
    capitalFutur: "~€14,300",
    proches: 32,
    color: "from-sunrise to-tenderness",
  },
  {
    age: "5 ans",
    year: 5,
    moments: 6,
    capitalInvesti: "€5,400",
    capitalFutur: "~€19,100",
    proches: 48,
    color: "from-tenderness to-babyBlue",
  },
  {
    age: "12 ans",
    year: 12,
    moments: 14,
    capitalInvesti: "€11,500",
    capitalFutur: "~€28,000",
    proches: 65,
    color: "from-babyBlue to-growth",
  },
  {
    age: "18 ans",
    year: 18,
    moments: 24,
    capitalInvesti: "€16,600",
    capitalFutur: "~€41,000",
    proches: 80,
    color: "from-growth to-future",
  },
];

const SaCagnotte = () => {
  const [activeStage, setActiveStage] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveStage((prev) => (prev + 1) % timelineStages.length);
    }, 6000);

    return () => clearInterval(interval);
  }, []);

  return (
    <section className="section-padding bg-babyBlue-light/10 relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute top-20 left-10 w-64 h-64 bg-legatia-green/10 rounded-full blur-3xl" />
      <div className="absolute bottom-20 right-10 w-80 h-80 bg-legatia-green/10 rounded-full blur-3xl" />

      <div className="container mx-auto relative z-10">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-block bg-legatia-green/20 text-legatia-green rounded-full px-6 py-2 mb-6">
            <span className="font-semibold">Comment ça marche</span>
          </div>
          <h2 className="section-title mb-4">Découvrez comment le compte Legatia de votre enfant grandit avec lui</h2>
          <p className="subtitle-hero max-w-3xl mx-auto">
            Vous créez le compte une fois. Des souvenirs immortalisés et des contributions investies de la naissance à ses 18 ans.
          </p>
        </div>

        {/* Progressive Timeline */}
        <div className="max-w-6xl mx-auto mb-20">
          {/* Timeline Line */}
          <div className="relative mb-12">
            <div className="absolute top-1/2 left-0 right-0 h-1 bg-legatia-green -translate-y-1/2" />
            
            {/* Timeline Points */}
            <div className="relative flex justify-between items-center">
              {timelineStages.map((stage, index) => (
                <button
                  key={index}
                  onClick={() => setActiveStage(index)}
                  className={`flex flex-col items-center transition-all ${
                    index === activeStage ? "scale-110" : "scale-100 opacity-60"
                  }`}
                >
                  <div
                    className={`w-16 h-16 rounded-full bg-legatia-green flex items-center justify-center mb-3 shadow-xl ${
                      index === activeStage ? "ring-4 ring-white shadow-2xl" : ""
                    }`}
                  >
                    <span className="text-white font-bold text-lg">{stage.year}</span>
                  </div>
                  <span className="text-sm font-semibold">{stage.age}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Active Stage Details */}
          <div className="bg-white rounded-3xl p-8 shadow-2xl">
            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center p-6 bg-sunrise-light rounded-2xl">
                <PartyPopper className="h-10 w-10 mx-auto mb-3 text-sunrise" />
                <div className="text-3xl font-bold mb-2">{timelineStages[activeStage].moments}</div>
                <div className="text-sm text-muted-foreground">Moments immortalisés</div>
              </div>

              <div className="text-center p-6 bg-legatia-green/10 rounded-2xl">
                <TrendingUp className="h-10 w-10 mx-auto mb-3 text-growth" />
                <div className="text-lg font-bold text-muted-foreground mb-1">Investi: {timelineStages[activeStage].capitalInvesti}</div>
                <div className="text-3xl font-bold mb-1">{timelineStages[activeStage].capitalFutur}</div>
                <div className="text-xs text-muted-foreground">Valeur estimée à 18 ans*</div>
              </div>

              <div className="text-center p-6 bg-babyBlue-light rounded-2xl">
                <Heart className="h-10 w-10 mx-auto mb-3 text-babyBlue" />
                <div className="text-3xl font-bold mb-2">{timelineStages[activeStage].proches}</div>
                <div className="text-sm text-muted-foreground">Proches (cumulé)</div>
              </div>
            </div>
            <p className="text-xs text-center text-muted-foreground mt-4">*Projection basée sur €16,600 investis sur 18 ans à 7-8%/an (historique indices mondiaux). Performances passées ne garantissent pas les résultats futurs.</p>
          </div>
        </div>

        {/* 3 Visual Cards */}
        <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {/* Card 1: Créer */}
          <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all group">
            <div className="w-16 h-16 bg-legatia-green rounded-full flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
              <Sparkles className="h-8 w-8 text-white" />
            </div>
            <h3 className="text-2xl font-bold mb-3">Vous créez son espace de vie</h3>
            <p className="text-muted-foreground mb-4">
              Vous ouvrez le compte Legatia de votre enfant pour raconter son histoire et construire son avenir. Une seule fois, 2 minutes.
            </p>
            <div className="bg-sunrise-light rounded-lg p-3 text-sm">
              <strong>✨ Souvenirs + Capital pour 18 ans</strong>
            </div>
          </div>

          {/* Card 2: Alimenter */}
          <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all group">
            <div className="w-16 h-16 bg-legatia-green rounded-full flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
              <Gift className="h-8 w-8 text-white" />
            </div>
            <h3 className="text-2xl font-bold mb-3">Vous racontez ET alimentez</h3>
            <p className="text-muted-foreground mb-4">
              Vous ajoutez photos, messages, vidéos + contributions financières pour chaque moment de vie.
            </p>
            <div className="space-y-2 text-sm">
              <div className="bg-babyBlue-light rounded-lg p-2">🎂 Album + €1,200</div>
              <div className="bg-growth-light rounded-lg p-2">🎄 Vidéos + €450</div>
              <div className="bg-tenderness-light rounded-lg p-2">🎓 Photos + €300</div>
            </div>
          </div>

          {/* Card 3: Capital */}
          <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all group">
            <div className="w-16 h-16 bg-legatia-green rounded-full flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
              <TrendingUp className="h-8 w-8 text-white" />
            </div>
            <h3 className="text-2xl font-bold mb-3">Il/elle reçoit : Histoire + Capital</h3>
            <p className="text-muted-foreground mb-4">
              À 18 ans : 18 ans de souvenirs immortalisés + un capital pour réaliser ses rêves.
            </p>
            <div className="bg-legatia-green/10 rounded-lg p-3 text-sm">
              <strong>📖 Album de vie complet + 💰 ~€41,000*</strong>
              <p className="text-xs text-muted-foreground mt-2">
                *€16,600 investis → ~€41,000 (7-8%/an historique). Performances passées ne garantissent pas les résultats futurs.
              </p>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center mt-12">
          <Button
            className="bg-legatia-green hover:bg-legatia-green/90 text-white font-semibold text-lg px-10 py-6 rounded-full shadow-2xl transform hover:scale-105 transition-all"
            onClick={() => window.location.href = "https://calendly.com/legatia/15min"}
          >
            Commencer son histoire
          </Button>
          <p className="text-sm text-muted-foreground mt-4">
            Gratuit • Sans engagement • 2 minutes
          </p>
        </div>
      </div>
    </section>
  );
};

export default SaCagnotte;